import React, { useEffect, useRef, useState } from 'react';
import domtoimage from 'dom-to-image';

import { UserAvatar } from 'components/user-avatar';
import { useAuth } from 'context/AuthContext';

import './style.scss';

import ovoIsolado from '../../assets/img/pascoa/ovo-isolado.png';
import { getEasterMessage } from 'services/content.service';
import { IoIosClose } from 'react-icons/io';
import Button from 'components/ui/button';
import { actionLog } from 'services/actionlog.service';

interface Props {
  closeModal: () => void;
}

const PascoaExtratoItem: React.FC<Props> = ({ closeModal }) => {
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(true);

  const { user, userInfo, setShowNotifications } = useAuth();

  const ovoRef = useRef<HTMLButtonElement>(null);
  const modalItemRef = useRef<HTMLDivElement>(null);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function loadData() {
      const data = await getEasterMessage();

      if (!data) {
        setLoading(false);
        return;
      }

      setMessage(data.message);

      if (data?.opened) {
        setOpened(data.opened);
        setShowNotifications(true);
      }

      setLoading(false);
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && ovoRef?.current && !opened) {
      ovoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      ovoRef.current.focus();
    }
  }, [loading, opened]);

  useEffect(() => {
    if (!loading && showModal && modalItemRef?.current) {
      modalItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      modalItemRef.current.focus();
    }
  }, [loading, showModal]);

  const handleDownload = async () => {
    if (!modalRef.current) return;

    sendActionLog('download');

    const modalEl = modalRef.current;

    // 1. Encontra a imagem do avatar que está gerando o erro de CORS
    const imgEl = modalEl.querySelector('img[src^="https://s3.amazonaws.com"]') as HTMLImageElement;

    if (imgEl) {
      try {
        const originalSrc = imgEl.src;

        // 2. Converte a URL global para formato regional
        const regionalSrc = originalSrc.replace(
          /^https:\/\/s3\.amazonaws\.com\/([^/]+)\/(.+)$/,
          'https://$1.s3.amazonaws.com/$2',
        );

        // 3. Tenta fazer o fetch da imagem usando a URL convertida
        const response = await fetch(regionalSrc, { mode: 'cors' });

        if (!response.ok) {
          throw new Error(`Erro ao buscar imagem convertida: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        // 4. Substitui a imagem original temporariamente
        imgEl.src = blobUrl;

        // 5. Gera a imagem com dom-to-image
        const dataUrl = await domtoimage.toPng(modalEl);

        // 6. Restaura a imagem original
        imgEl.src = originalSrc;
        URL.revokeObjectURL(blobUrl); // limpa o blob temporário

        // 7. Baixa o resultado
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = 'card.png';
        link.click();
      } catch (error) {
        console.error('Erro ao gerar a imagem:', error);
      }
    } else {
      console.warn('Imagem externa não encontrada no modal.');
      // Caso não haja imagem externa, gera normalmente
      try {
        const dataUrl = await domtoimage.toPng(modalEl);
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = 'card.png';
        link.click();
      } catch (error) {
        console.error('Erro ao gerar imagem sem avatar externo:', error);
      }
    }
  };

  function capitalizeWords(text: string | undefined): string {
    if (!text) return '';

    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  function sendActionLog(interaction: string) {
    actionLog(
      'me',
      'easter',
      {
        interaction,
      },
      null,
      null,
      null,
      false,
    );
  }

  if (loading) return null;

  return (
    <div className="container-pascoa">
       <div className="modal-overlay">
          <div ref={modalItemRef} className="modal-card bb-theme">
            <Button
              color="primary"
              className="absolute -right-5 -top-5  rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center"
              onClick={closeModal}
              arialabel="Fechar modal de páscoa"
            >
              <IoIosClose color="white" size={30} />
            </Button>

            <div ref={modalRef} className="relative w-full h-full bg-gradiente-custom p-16 overflow-hidden">
              {/* Forma decorativa grande no canto superior direito */}
              <div className="absolute top-0 -right-0 w-5 h-60 bg-[#feee89] rounded-bl-[20px] " />

              {/* Forma decorativa pequena no canto inferior esquerdo */}
              <div className="absolute bottom-0 -left-[100px] w-36 h-80 bg-[#feee89] rounded-tr-[30px] " />

              {/* Conteúdo do card centralizado */}
              <div className="card-content bg-white rounded-lg relative h-100 pb-24 shadow-2xl">
                <div className="absolute top-[35px] -left-[60px] cursor-none">
                  <div className="bunny-ears" />
                  <div className="avatar-wrapper">
                    <UserAvatar responsive data={{ ...user, ...user?.extra }} />
                  </div>
                </div>

                <div className="bg-[#465eff] rounded-t-lg p-4 mb-4" tabIndex={0}>
                  <h1 className="leading-none text-2xl font-bold px-4">
                    {message ? 'Veja como seus líderes e colegas te reconhecem!' : 'Feliz Páscoa'}
                  </h1>
                </div>

                <div className="card-content px-5" tabIndex={0}>
                  <h3 className="leading-none text-[#465eff] font-bold text-lg px-4">
                    {capitalizeWords(userInfo?.nome_guerra)},
                  </h3>
                  <p className="text-sm text-[#465eff] leading-tight mt-5">
                    {message
                      ? message
                      : 'Neste período de Páscoa, desejo a todos momentos de renovação, esperança e reflexão. Que possamos aproveitar este tempo para recarregar nossas energias e fortalecer nossos laços de união e colaboração.'}
                  </p>
                </div>

                <div className="absolute -bottom-[30px] -left-10 w-28 -rotate-[30deg]">
                  <img src={ovoIsolado} alt="Cenoura" />
                </div>

                <div className="absolute -bottom-[30px] -right-10 w-28 rotate-[30deg]">
                  <img src={ovoIsolado} alt="Ovo isolado" className="ovo-isolado" />
                </div>
              </div>
            </div>

            <div className="mt-5 flex-col  flex items-center justify-center">
              <button
                onClick={handleDownload}
                className="w-1/2 bg-purple text-center text-white hover:text-white active:text-white focus:text-white hover:ring hover:ring-white rounded-md shadow-xl font-bold px-6 py-3 uppercase inline-block no-underline hover:no-underline active:no-underline focus:no-underline"
              >
                <span className="p-1 leading-tight">Baixar imagem</span>
              </button>

              {message && (
                <p className="text-disclaimer">
                  Este conteúdo foi gerado por inteligência artificial com base nos comentários recebidos. Ressaltamos que, apesar
                  dos esforços para garantir precisão, a IA pode apresentar falhas.
                </p>
              )}
            </div>
          </div>
        </div>
    </div>
  );
};

export default PascoaExtratoItem;
